import { ApiUploadError, type StaffCatalogApi } from "@lafayette/api-client";
import type { StaffCatalogUploadTarget } from "@lafayette/schemas";
import { createContext, type ReactNode, useContext } from "react";

// What the catalog pages need from outside: the typed API client, the way a file reaches storage, and
// the clock. The admin app supplies the real ones (`admin-runtime.ts`); a test supplies a fake API.

export type UploadFile = (target: StaffCatalogUploadTarget, file: Blob) => Promise<void>;

export type CatalogRuntime = Readonly<{
  api: StaffCatalogApi;
  uploadFile: UploadFile;
  now: () => Date;
}>;

const RuntimeContext = createContext<CatalogRuntime | null>(null);

export function CatalogRuntimeProvider({
  value,
  children,
}: Readonly<{ value: CatalogRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useCatalogRuntime(): CatalogRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The catalog pages need a CatalogRuntimeProvider.");
  return runtime;
}

/**
 * The browser's own request to storage, with the URL, method and headers of the signed target. The file
 * goes straight to storage: it never passes the Lafayette API. The target belongs to one exact path.
 */
export async function putFileToTarget(
  target: StaffCatalogUploadTarget,
  file: Blob,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  let response: Response;
  try {
    response = await fetcher(target.url, {
      method: target.method,
      headers: target.headers,
      body: file,
    });
  } catch (cause) {
    // No answer from storage. The message never holds the signed URL.
    throw new ApiUploadError(0, "storage did not answer", { cause });
  }
  if (!response.ok) {
    throw new ApiUploadError(response.status, `storage answered ${response.status}`);
  }
}
